// src/pages/ConferenciaFarma.js — Sistema de Conferência (layout reformulado)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, FormControl,
  InputLabel, Select, Chip, IconButton, CircularProgress, Alert,
  Divider, Stack, Avatar, InputAdornment, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid
} from '@mui/material';
import {
  DoneAll as DoneAllIcon, Business as BusinessIcon,
  MedicalServices as MedIcon, Refresh as RefreshIcon,
  FilterAlt as FilterIcon, Clear as ClearIcon,
  SortByAlpha as SortIcon, Print as PrintIcon,
  FileDownload as ExportIcon, Search as SearchIcon,
  CheckCircle as CheckCircleIcon, HourglassTop as HourglassIcon,
  ReportProblem as WarningIcon, Visibility as VisibilityIcon,
  CalendarMonth as CalendarIcon, Undo as UndoIcon,
  ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon,
  ZoomOutMap as ZoomResetIcon, RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon, Fullscreen as FullscreenIcon,
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
  Download as DownloadIcon, Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CFG = {
  PENDENTE:     { label: 'Pendente',     bg: '#fef3c7', fg: '#92400e', icon: <HourglassIcon sx={{ color: '#f59e0b' }} /> },
  CONFERIDO:    { label: 'Conferida',    bg: '#d1fae5', fg: '#065f46', icon: <CheckCircleIcon sx={{ color: '#10b981' }} /> },
  DIVERGENTE:   { label: 'Divergente',   bg: '#fee2e2', fg: '#991b1b', icon: <WarningIcon sx={{ color: '#ef4444' }} /> },
  EM_APROVACAO: { label: 'Em Aprovação', bg: '#dbeafe', fg: '#1e40af', icon: <HourglassIcon sx={{ color: '#3b82f6' }} /> }
};

const formatarData = (d) => {
  if (!d) return '—';
  // O driver agora entrega a data ja no fuso local (useUTC:false em
  // config/sqlserver.js), entao o parse direto basta. Antes daqui havia um
  // remendo que arrancava o 'Z' da string — mantido ate 19/08/2026, quando a
  // causa foi corrigida na conexao. Sem remover, esta tela passaria a mostrar
  // 3h A MAIS.
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  const dia = String(dt.getDate()).padStart(2, '0');
  const mes = String(dt.getMonth() + 1).padStart(2, '0');
  const ano = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${ano}, ${hh}:${mm}`;
};

const ConferenciaFarma = () => {
  const { api, user } = useAuth();

  const [filiais, setFiliais] = useState([]);
  const [filialSelecionada, setFilialSelecionada] = useState('');
  const [filialCarregada, setFilialCarregada] = useState(null); // filial com dados carregados
  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filtros, setFiltros] = useState({
    status: '',
    data_inicio: '', hora_inicio: '',
    data_fim: '', hora_fim: '',
    search: ''
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [ordenarNumero, setOrdenarNumero] = useState(false);

  // Modal detalhe
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [receitaSelecionada, setReceitaSelecionada] = useState(null);
  const [imagens, setImagens] = useState([]);
  const [statusAtual, setStatusAtual] = useState(null);
  const [acaoEmAndamento, setAcaoEmAndamento] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [imgScale, setImgScale] = useState(1);
  const [imgRotation, setImgRotation] = useState(0);

  // Modal de Nova Divergência (dentro do modal de Conferência)
  const [divModalAberto, setDivModalAberto] = useState(false);
  const [divForm, setDivForm] = useState({
    tipo_divergencia: '', descricao_divergencia: '',
    prioridade: 'media', valor_original: ''
  });
  const [divSalvando, setDivSalvando] = useState(false);
  const [comentarioAprovacao, setComentarioAprovacao] = useState('');
  const [rejeitarModalAberto, setRejeitarModalAberto] = useState(false);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  const carregarFiliais = useCallback(async () => {
    try {
      const resp = await api.listarFiliais();
      if (resp.success) setFiliais(resp.dados || []);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { carregarFiliais(); }, [carregarFiliais]);

  // Auto-selecionar filial do usuário ao abrir a página
  useEffect(() => {
    if (user?.filial_id && !filialSelecionada && !filialCarregada) {
      setFilialSelecionada(user.filial_id);
      carregarReceitas(user.filial_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const carregarReceitas = useCallback(async (filialId) => {
    if (!filialId) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        filial_id: filialId,
        limit: 500,
        ...filtrosAplicados
      };
      // Monta data_inicio / data_fim com hora
      if (filtrosAplicados.data_inicio) {
        params.data_inicio = `${filtrosAplicados.data_inicio}T${filtrosAplicados.hora_inicio || '00:00'}:00`;
      }
      if (filtrosAplicados.data_fim) {
        params.data_fim = `${filtrosAplicados.data_fim}T${filtrosAplicados.hora_fim || '23:59'}:59`;
      }
      delete params.hora_inicio;
      delete params.hora_fim;

      const resp = await api.conferenciaFarmaListar(params);
      if (resp.success) {
        setReceitas(resp.dados?.receitas || []);
        setFilialCarregada(filialId);
      } else {
        setError(resp.message || 'Erro ao carregar receitas');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [api, filtrosAplicados]);

  const handleCarregar = () => {
    if (!filialSelecionada) {
      setError('Selecione uma filial');
      return;
    }
    carregarReceitas(filialSelecionada);
  };

  const handleAplicarFiltros = () => {
    setFiltrosAplicados({
      status: filtros.status,
      data_inicio: filtros.data_inicio,
      hora_inicio: filtros.hora_inicio,
      data_fim: filtros.data_fim,
      hora_fim: filtros.hora_fim
    });
  };

  const handleLimpar = () => {
    setFiltros({
      status: '', data_inicio: '', hora_inicio: '',
      data_fim: '', hora_fim: '', search: ''
    });
    setFiltrosAplicados({});
  };

  // Recarrega quando filtros aplicados mudam
  useEffect(() => {
    if (filialCarregada) carregarReceitas(filialCarregada);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAplicados]);

  // Lista filtrada client-side por busca + ordenação
  const receitasExibidas = useMemo(() => {
    let lista = [...receitas];
    const q = (filtros.search || '').trim().toLowerCase();
    if (q.length >= 3) {
      lista = lista.filter(r =>
        String(r.NRRQU).includes(q) ||
        (r.observacoes || '').toLowerCase().includes(q) ||
        (r.conferido_por || '').toLowerCase().includes(q)
      );
    }
    if (ordenarNumero) {
      lista.sort((a, b) => (Number(a.NRRQU) || 0) - (Number(b.NRRQU) || 0));
    } else {
      lista.sort((a, b) => new Date(b.data_upload) - new Date(a.data_upload));
    }
    return lista;
  }, [receitas, filtros.search, ordenarNumero]);

  const filialInfo = useMemo(
    () => filiais.find(f => (f.codigo ?? f.id) === Number(filialCarregada)),
    [filiais, filialCarregada]
  );

  const exportarCSV = () => {
    const header = ['Status', 'NRRQU', 'Série', 'Data Upload', 'Conferido Por', 'Data Conferência', 'Total Imagens'];
    const rows = receitasExibidas.map(r => [
      STATUS_CFG[r.status_calculado]?.label || r.status_calculado,
      r.NRRQU,
      r.serier || '',
      formatarData(r.data_upload),
      r.conferido_por || '',
      formatarData(r.data_conferencia),
      r.total_imagens
    ]);
    const csv = [header, ...rows].map(row =>
      row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conferencia_${filialInfo?.nome_filial || 'filial'}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const abrirDetalhe = async (receita) => {
    setReceitaSelecionada(receita);
    setDetalheAberto(true);
    setImagens([]);
    setStatusAtual(null);
    setImgIdx(0);
    setImgScale(1);
    setImgRotation(0);
    setComentarioAprovacao('');
    try {
      const [imgResp, statusResp] = await Promise.all([
        api.conferenciaFarmaImagens(receita.NRRQU, receita.cdfil),
        api.conferenciaFarmaStatus(receita.NRRQU, receita.cdfil)
      ]);
      if (imgResp.success) setImagens(imgResp.dados?.imagens || []);
      if (statusResp.success) setStatusAtual(statusResp.dados);
    } catch (e) { console.error(e); }
  };

  const fecharDetalhe = () => {
    setDetalheAberto(false);
    setReceitaSelecionada(null);
    setImagens([]);
    setStatusAtual(null);
  };

  const imgAtual = imagens[imgIdx] || null;
  const urlAtual = imgAtual?.signed_url || imgAtual?.url_imagem || imgAtual?.imageUrl;
  const ehPdf = urlAtual && (imgAtual?.tipo_mime === 'application/pdf' || urlAtual.toLowerCase().includes('.pdf'));

  const proximaImg = () => {
    setImgScale(1); setImgRotation(0);
    setImgIdx(i => (i + 1) % imagens.length);
  };
  const anteriorImg = () => {
    setImgScale(1); setImgRotation(0);
    setImgIdx(i => (i - 1 + imagens.length) % imagens.length);
  };

  const baixarImagem = async () => {
    if (!urlAtual) return;
    try {
      const resp = await fetch(urlAtual);
      const blob = await resp.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = imgAtual.nome_original || imgAtual.nome_arquivo || `receita_${receitaSelecionada.NRRQU}.${ehPdf ? 'pdf' : 'jpg'}`;
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      setError('Erro ao baixar imagem: ' + e.message);
    }
  };

  const imprimirImagem = () => {
    if (!urlAtual) return;
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Imprimir #${receitaSelecionada.NRRQU}</title>
      <style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh}
      img{max-width:100%;max-height:100vh}</style></head>
      <body><img src="${urlAtual}" onload="window.print();window.close()"/></body></html>
    `);
  };

  const abrirNovaDivergencia = () => {
    setDivForm({ tipo_divergencia: '', descricao_divergencia: '', prioridade: 'media', valor_original: '' });
    setDivModalAberto(true);
  };

  const aprovarDivergencia = async () => {
    if (!statusAtual?.divergencia_id) return;
    setAcaoEmAndamento(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaAprovar(statusAtual.divergencia_id, {
        comentario_aprovacao: comentarioAprovacao.trim() || null
      });
      if (resp.success) {
        setSuccess('Divergência aprovada — receita conferida.');
        fecharDetalhe();
        await carregarReceitas(filialCarregada);
      } else {
        setError(resp.message || 'Erro ao aprovar');
      }
    } catch (e) {
      setError(e.message || 'Erro ao aprovar');
    } finally {
      setAcaoEmAndamento(false);
    }
  };

  const abrirRejeitarModal = () => {
    setMotivoRejeicao('');
    setError('');
    setRejeitarModalAberto(true);
  };

  const confirmarRejeicao = async () => {
    if (!statusAtual?.divergencia_id) return;
    if (motivoRejeicao.trim().length < 10) {
      setError('Motivo da rejeição precisa ter no mínimo 10 caracteres');
      return;
    }
    setAcaoEmAndamento(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaRejeitar(statusAtual.divergencia_id, {
        comentario_aprovacao: motivoRejeicao.trim()
      });
      if (resp.success) {
        setSuccess('Divergência rejeitada — devolvida à loja.');
        setRejeitarModalAberto(false);
        fecharDetalhe();
        await carregarReceitas(filialCarregada);
      } else {
        setError(resp.message || 'Erro ao rejeitar');
      }
    } catch (e) {
      setError(e.message || 'Erro ao rejeitar');
    } finally {
      setAcaoEmAndamento(false);
    }
  };

  const salvarNovaDivergencia = async () => {
    if (!receitaSelecionada) return;
    if (!divForm.descricao_divergencia || divForm.descricao_divergencia.trim().length < 10) {
      setError('Descrição precisa ter no mínimo 10 caracteres');
      return;
    }
    setDivSalvando(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.divergenciaCriar({
        numero_requisicao: String(receitaSelecionada.NRRQU),
        filial_id: receitaSelecionada.cdfil,
        tipo_divergencia: divForm.tipo_divergencia || null,
        descricao_divergencia: divForm.descricao_divergencia.trim(),
        prioridade: divForm.prioridade,
        valor_original: divForm.valor_original || null
      });
      if (resp.success) {
        setSuccess(resp.message || 'Divergência registrada');
        setDivModalAberto(false);
        // Recarrega status da receita pra refletir
        try {
          const st = await api.conferenciaFarmaStatus(receitaSelecionada.NRRQU, receitaSelecionada.cdfil);
          if (st.success) setStatusAtual(st.dados);
        } catch (_) {}
        await carregarReceitas(filialCarregada);
        fecharDetalhe();
      } else {
        setError(resp.message || 'Erro ao registrar divergência');
      }
    } catch (e) {
      setError(e.message || 'Erro de conexão');
    } finally {
      setDivSalvando(false);
    }
  };

  const conferirReceita = async () => {
    if (!receitaSelecionada) return;
    setAcaoEmAndamento(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.conferenciaFarmaConferir(receitaSelecionada.NRRQU, receitaSelecionada.cdfil);
      if (resp.success) {
        setSuccess('Receita conferida com sucesso');
        fecharDetalhe();
        await carregarReceitas(filialCarregada);
      } else {
        setError(resp.message || 'Erro ao conferir');
      }
    } catch (e) {
      setError(e.message || 'Erro ao conferir');
    } finally {
      setAcaoEmAndamento(false);
    }
  };

  const reverterConferencia = async () => {
    if (!receitaSelecionada) return;
    if (!window.confirm('Reverter a conferência desta receita?')) return;
    setAcaoEmAndamento(true);
    setError(''); setSuccess('');
    try {
      const resp = await api.conferenciaFarmaReverter(
        receitaSelecionada.NRRQU, receitaSelecionada.cdfil,
        statusAtual?.divergencia_id || null
      );
      // Backend pode pedir escolha de divergência (múltiplas aprovadas) sem reverter nada.
      if (resp.success && resp.action === 'choose') {
        setError(resp.message || 'Há múltiplas divergências aprovadas. Selecione qual reabrir.');
      } else if (resp.success) {
        setSuccess('Conferência revertida');
        fecharDetalhe();
        await carregarReceitas(filialCarregada);
      } else {
        setError(resp.message || 'Erro ao reverter');
      }
    } catch (e) {
      setError(e.message || 'Erro ao reverter');
    } finally {
      setAcaoEmAndamento(false);
    }
  };

  return (
    <Box sx={{ '@media print': { '& .no-print': { display: 'none' } } }}>
      {/* Header com gradiente */}
      <Paper
        elevation={0}
        sx={{
          p: 3, mb: 2, borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}
        className="no-print"
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">Sistema de Conferência</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Conferência de prescrições e requisições farmacêuticas
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
          <DoneAllIcon sx={{ color: '#fbbf24', fontSize: 32 }} />
        </Avatar>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError('')} className="no-print" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} className="no-print" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Selecionar Filial */}
      <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <BusinessIcon sx={{ color: '#64748b', mr: 1 }} />
          <Typography variant="h6" fontWeight="bold">Selecionar Filial</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 320, flex: 1 }} size="small">
            <Select
              value={filialSelecionada}
              onChange={(e) => setFilialSelecionada(e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>Selecione uma filial</em></MenuItem>
              {filiais.map(f => (
                <MenuItem key={f.id} value={f.codigo ?? f.id}>
                  {f.nome_filial}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleCarregar}
            disabled={loading || !filialSelecionada}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            sx={{
              bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' },
              px: 3, py: 1.2, fontWeight: 'bold'
            }}
          >
            Carregar Receitas
          </Button>
        </Box>
      </Paper>

      {/* Lista de receitas */}
      {filialCarregada && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Cabeçalho lista */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MedIcon sx={{ color: '#64748b', mr: 1 }} />
              <Typography variant="h6" fontWeight="bold">Receitas para Conferência</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} className="no-print">
              <Typography variant="body2" color="text.secondary">
                {filialInfo?.nome_filial || filialCarregada} ({receitasExibidas.length})
              </Typography>
              <Button
                size="small" variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => carregarReceitas(filialCarregada)}
              >
                Atualizar
              </Button>
            </Box>
          </Box>

          {/* Filtros */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8fafc' }} className="no-print">
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" fontWeight="bold">Status:</Typography>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={filtros.status}
                  onChange={(e) => setFiltros(p => ({ ...p, status: e.target.value }))}
                  displayEmpty
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="PENDENTE">Pendente</MenuItem>
                  <MenuItem value="CONFERIDO">Conferida</MenuItem>
                  <MenuItem value="DIVERGENTE">Divergente</MenuItem>
                  <MenuItem value="EM_APROVACAO">Em Aprovação</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" fontWeight="bold">De:</Typography>
              <TextField
                size="small" type="date" sx={{ width: 150 }}
                value={filtros.data_inicio}
                onChange={(e) => setFiltros(p => ({ ...p, data_inicio: e.target.value }))}
              />
              <TextField
                size="small" type="time" sx={{ width: 110 }}
                value={filtros.hora_inicio}
                onChange={(e) => setFiltros(p => ({ ...p, hora_inicio: e.target.value }))}
              />

              <Typography variant="body2" fontWeight="bold">Até:</Typography>
              <TextField
                size="small" type="date" sx={{ width: 150 }}
                value={filtros.data_fim}
                onChange={(e) => setFiltros(p => ({ ...p, data_fim: e.target.value }))}
              />
              <TextField
                size="small" type="time" sx={{ width: 110 }}
                value={filtros.hora_fim}
                onChange={(e) => setFiltros(p => ({ ...p, hora_fim: e.target.value }))}
              />

              <Button
                variant="contained" size="small" startIcon={<FilterIcon />}
                onClick={handleAplicarFiltros}
                sx={{ bgcolor: '#1e293b', '&:hover': { bgcolor: '#0f172a' } }}
              >
                Aplicar
              </Button>
              <Button
                variant="outlined" size="small" startIcon={<ClearIcon />}
                onClick={handleLimpar} color="inherit"
              >
                Limpar
              </Button>
              <Button
                variant={ordenarNumero ? 'contained' : 'outlined'} size="small"
                startIcon={<SortIcon />}
                onClick={() => setOrdenarNumero(v => !v)}
                sx={ordenarNumero ? { bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } } : {}}
              >
                Ordenar por Número
              </Button>
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
              <Button
                variant="contained" size="small" startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
              >
                Imprimir
              </Button>
              <Button
                variant="contained" size="small" startIcon={<ExportIcon />}
                onClick={exportarCSV}
                sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
              >
                Exportar CSV
              </Button>
            </Stack>

            <TextField
              fullWidth size="small" sx={{ mt: 1.5, bgcolor: 'white' }}
              placeholder="Buscar por número, observações..."
              value={filtros.search}
              onChange={(e) => setFiltros(p => ({ ...p, search: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      ℹ Min. 3 caracteres
                    </Typography>
                  </InputAdornment>
                )
              }}
            />
          </Paper>

          {/* Lista */}
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : receitasExibidas.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">Nenhuma receita encontrada</Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {receitasExibidas.map((r) => {
                const cfg = STATUS_CFG[r.status_calculado] || STATUS_CFG.PENDENTE;
                return (
                  <Paper
                    key={`${r.NRRQU}-${r.cdfil}`}
                    variant="outlined"
                    onClick={() => abrirDetalhe(r)}
                    sx={{
                      px: 2, py: 1.5, display: 'flex', alignItems: 'center',
                      gap: 2, transition: 'background 0.15s',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f8fafc', borderColor: '#6366f1' }
                    }}
                  >
                    <Chip
                      label={cfg.label}
                      size="small"
                      sx={{ bgcolor: cfg.bg, color: cfg.fg, fontWeight: 'bold', minWidth: 96 }}
                    />
                    <Typography
                      sx={{ fontFamily: 'monospace', fontWeight: 'bold', minWidth: 120 }}
                    >
                      #{r.NRRQU}{r.serier ? `-${r.serier}` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="body2">{formatarData(r.data_upload)}</Typography>
                    </Box>
                    {r.conferido_por && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                        por {r.conferido_por}
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={cfg.label}>
                      <IconButton size="small" onClick={(e) => e.stopPropagation()}>{cfg.icon}</IconButton>
                    </Tooltip>
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); abrirDetalhe(r); }}
                        className="no-print">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* Modal detalhe (mantido da versão anterior) */}
      <Dialog open={detalheAberto} onClose={fecharDetalhe} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">Conferência de Receita</Typography>
              <Typography variant="caption" color="text.secondary">
                NRRQU:{' '}
                <Box component="span" sx={{
                  fontFamily: 'monospace', bgcolor: 'grey.100',
                  px: 1, py: 0.25, borderRadius: 0.5, ml: 0.5
                }}>
                  {receitaSelecionada?.NRRQU}{receitaSelecionada?.serier ? `-${receitaSelecionada.serier}` : ''}
                </Box>
              </Typography>
            </Box>
            <IconButton onClick={fecharDetalhe}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {receitaSelecionada && (
            <Grid container sx={{ minHeight: '70vh' }}>
              {/* ======= COLUNA ESQUERDA — IMAGEM ======= */}
              <Grid item xs={12} md={9} sx={{ bgcolor: 'grey.100', p: 2, position: 'relative' }}>
                <Box sx={{
                  bgcolor: 'grey.200', borderRadius: 2, height: '100%',
                  minHeight: 520, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', position: 'relative', overflow: 'hidden'
                }}>
                  {imagens.length === 0 ? (
                    <Typography color="text.secondary">Nenhuma imagem cadastrada</Typography>
                  ) : ehPdf ? (
                    <Box component="iframe" src={urlAtual} sx={{ width: '100%', height: '100%', minHeight: 520, border: 0 }} />
                  ) : (
                    <Box
                      component="img"
                      src={urlAtual}
                      alt={imgAtual?.nome_original}
                      sx={{
                        maxWidth: '100%', maxHeight: '100%',
                        transform: `scale(${imgScale}) rotate(${imgRotation}deg)`,
                        transition: 'transform 0.2s',
                        userSelect: 'none'
                      }}
                    />
                  )}

                  {/* Controles de zoom/rotação */}
                  {imagens.length > 0 && !ehPdf && (
                    <Box sx={{
                      position: 'absolute', top: 12, left: 12,
                      display: 'flex', flexDirection: 'column', gap: 0.5
                    }}>
                      {[
                        { icon: <ZoomInIcon />, title: 'Zoom +', onClick: () => setImgScale(s => Math.min(s + 0.25, 4)) },
                        { icon: <ZoomOutIcon />, title: 'Zoom -', onClick: () => setImgScale(s => Math.max(s - 0.25, 0.25)) },
                        { icon: <ZoomResetIcon />, title: 'Reset', onClick: () => { setImgScale(1); setImgRotation(0); } },
                        { icon: <RotateLeftIcon />, title: 'Girar -90°', onClick: () => setImgRotation(r => r - 90) },
                        { icon: <RotateRightIcon />, title: 'Girar +90°', onClick: () => setImgRotation(r => r + 90) },
                        { icon: <FullscreenIcon />, title: 'Abrir em nova aba', onClick: () => window.open(urlAtual, '_blank') }
                      ].map((btn, i) => (
                        <Tooltip key={i} title={btn.title} placement="right">
                          <IconButton size="small" onClick={btn.onClick}
                            sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}>
                            {btn.icon}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  )}

                  {/* Navegação entre imagens */}
                  {imagens.length > 1 && (
                    <>
                      <IconButton onClick={anteriorImg} sx={{
                        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.55)', color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                      }}>
                        <ChevronLeftIcon />
                      </IconButton>
                      <IconButton onClick={proximaImg} sx={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.55)', color: 'white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                      }}>
                        <ChevronRightIcon />
                      </IconButton>
                      <Chip
                        label={
                          <>
                            {imgIdx + 1} / {imagens.length}
                            {imgAtual?.serier && (
                              <Box component="span" sx={{
                                ml: 1, px: 0.75, py: 0.25, borderRadius: 0.5,
                                bgcolor: '#2563eb', fontSize: '0.7rem', fontWeight: 'bold'
                              }}>
                                {imgAtual.serier}
                              </Box>
                            )}
                          </>
                        }
                        sx={{
                          position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                          bgcolor: 'rgba(0,0,0,0.55)', color: 'white'
                        }}
                      />
                    </>
                  )}
                </Box>
              </Grid>

              {/* ======= COLUNA DIREITA — INFO + AÇÕES ======= */}
              <Grid item xs={12} md={3} sx={{ p: 2, bgcolor: 'grey.50', borderLeft: '1px solid', borderColor: 'divider', overflowY: 'auto', maxHeight: '80vh' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Informações</Typography>
                <Stack spacing={1.2} sx={{ mb: 3, fontSize: '0.88rem' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">NRRQU:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {receitaSelecionada.NRRQU}{receitaSelecionada.serier ? `-${receitaSelecionada.serier}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Filial:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {receitaSelecionada.nome_filial || receitaSelecionada.cdfil}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Data/Hora:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatarData(receitaSelecionada.data_upload)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip
                      size="small"
                      label={STATUS_CFG[receitaSelecionada.status_calculado]?.label}
                      sx={{
                        bgcolor: STATUS_CFG[receitaSelecionada.status_calculado]?.bg,
                        color: STATUS_CFG[receitaSelecionada.status_calculado]?.fg,
                        fontWeight: 'bold', height: 22
                      }}
                    />
                  </Box>
                  {receitaSelecionada.conferido_por && (
                    <>
                      <Divider sx={{ my: 0.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Conferido por:</Typography>
                        <Typography variant="body2" fontWeight="bold">{receitaSelecionada.conferido_por}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="text.secondary">Data conf.:</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatarData(receitaSelecionada.data_conferencia)}</Typography>
                      </Box>
                    </>
                  )}
                </Stack>

                {/* Observações */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Observações</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mb: 3, minHeight: 56 }}>
                  {receitaSelecionada.observacoes ? (
                    <Typography variant="body2">{receitaSelecionada.observacoes}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">Nenhuma observação</Typography>
                  )}
                </Paper>

                {/* Divergência ativa */}
                {statusAtual?.divergencia_id && (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 3, borderLeft: '4px solid', borderLeftColor: 'error.main' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      Divergência ativa
                    </Typography>
                    <Stack spacing={0.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">Status:</Typography>
                        <Chip size="small" label={statusAtual.divergencia_status} color="error" sx={{ height: 20 }} />
                      </Box>
                      {statusAtual.divergencia_tipo && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Tipo:</Typography>
                          <Typography variant="caption">{statusAtual.divergencia_tipo}</Typography>
                        </Box>
                      )}
                      {statusAtual.divergencia_descricao && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Descrição:</Typography>
                          <Typography variant="body2">{statusAtual.divergencia_descricao}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                )}

                {/* Ações */}
                <Stack spacing={1.5}>
                  {receitaSelecionada.status_calculado === 'PENDENTE' && (
                    <Button
                      fullWidth variant="contained" size="large"
                      startIcon={<CheckCircleIcon />}
                      disabled={acaoEmAndamento || !!statusAtual?.divergencia_id}
                      onClick={conferirReceita}
                      sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, py: 1.5 }}
                    >
                      Conferir Receita
                    </Button>
                  )}

                  {receitaSelecionada.status_calculado === 'CONFERIDO' && (
                    <Button
                      fullWidth variant="contained" size="large"
                      startIcon={<CheckCircleIcon />} disabled
                      sx={{ bgcolor: '#86efac !important', color: '#065f46 !important', py: 1.5 }}
                    >
                      Já Conferida
                    </Button>
                  )}

                  <Button
                    fullWidth variant="contained" size="large"
                    startIcon={<WarningIcon />}
                    disabled={acaoEmAndamento || !!statusAtual?.divergencia_id}
                    onClick={abrirNovaDivergencia}
                    sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' }, py: 1.5 }}
                  >
                    Nova Divergência
                  </Button>

                  {receitaSelecionada.status_calculado === 'CONFERIDO' && (
                    <Button
                      fullWidth variant="contained" size="large"
                      startIcon={<UndoIcon />}
                      disabled={acaoEmAndamento}
                      onClick={reverterConferencia}
                      sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' }, py: 1.5 }}
                    >
                      Reverter Conferência
                    </Button>
                  )}

                  {receitaSelecionada.status_calculado === 'EM_APROVACAO' && statusAtual?.divergencia_id && (
                    <>
                      {/* Botão info de "Aguardando Aprovação" */}
                      <Button
                        fullWidth variant="contained" size="large" disabled
                        startIcon={<HourglassIcon />}
                        sx={{ bgcolor: '#86efac !important', color: '#065f46 !important', py: 1.5 }}
                      >
                        Aguardando Aprovação
                      </Button>

                      {/* Aprovar + Rejeitar lado a lado */}
                      <Stack direction="row" spacing={1}>
                        <Button
                          fullWidth variant="contained" size="large"
                          startIcon={<CheckCircleIcon />}
                          disabled={acaoEmAndamento}
                          onClick={aprovarDivergencia}
                          sx={{ bgcolor: '#15803d', '&:hover': { bgcolor: '#166534' }, py: 1.5 }}
                        >
                          Aprovar Resolução
                        </Button>
                        <Button
                          fullWidth variant="contained" size="large"
                          startIcon={<WarningIcon />}
                          disabled={acaoEmAndamento}
                          onClick={abrirRejeitarModal}
                          sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, py: 1.5 }}
                        >
                          Rejeitar
                        </Button>
                      </Stack>

                      {/* Comentário opcional pra aprovação */}
                      <TextField
                        fullWidth multiline rows={2} size="small"
                        placeholder="Comentário (opcional)"
                        value={comentarioAprovacao}
                        onChange={(e) => setComentarioAprovacao(e.target.value)}
                        sx={{ bgcolor: 'white' }}
                      />
                    </>
                  )}

                  <Button
                    fullWidth variant="contained" size="large"
                    startIcon={<DownloadIcon />}
                    disabled={!urlAtual}
                    onClick={baixarImagem}
                    sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, py: 1.5 }}
                  >
                    Baixar Imagem
                  </Button>

                  <Button
                    fullWidth variant="contained" size="large"
                    startIcon={<PrintIcon />}
                    disabled={!urlAtual}
                    onClick={imprimirImagem}
                    sx={{ bgcolor: '#4b5563', '&:hover': { bgcolor: '#374151' }, py: 1.5 }}
                  >
                    Imprimir
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Rejeitar Resolução */}
      <Dialog open={rejeitarModalAberto} onClose={() => setRejeitarModalAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          bgcolor: '#dc2626', color: 'white', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon />
            <Typography variant="h6" fontWeight="bold">Rejeitar Resolução</Typography>
          </Box>
          <IconButton onClick={() => setRejeitarModalAberto(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            Explique o motivo da rejeição. A divergência voltará para status{' '}
            <strong>"Divergente"</strong> para que a loja possa resolver novamente.
          </Alert>

          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Motivo da Rejeição <span style={{ color: '#dc2626' }}>*</span>
          </Typography>
          <TextField
            fullWidth multiline rows={4}
            placeholder="Descreva o motivo da rejeição (mínimo 10 caracteres)…"
            value={motivoRejeicao}
            onChange={(e) => setMotivoRejeicao(e.target.value)}
            helperText="Mínimo de 10 caracteres"
            sx={{ mb: 2 }}
          />

          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Divergência:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                #{statusAtual?.divergencia_id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Requisição:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {receitaSelecionada?.NRRQU}
              </Typography>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined" color="inherit"
            startIcon={<CloseIcon />}
            onClick={() => setRejeitarModalAberto(false)}
            disabled={acaoEmAndamento}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={confirmarRejeicao}
            disabled={acaoEmAndamento || motivoRejeicao.trim().length < 10}
            sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
          >
            {acaoEmAndamento ? 'Processando…' : 'Confirmar Rejeição'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={divModalAberto} onClose={() => setDivModalAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nova Divergência — Req #{receitaSelecionada?.NRRQU}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tipo de divergência"
              placeholder="Ex: dados_incompletos, receita_ilegivel…"
              fullWidth
              value={divForm.tipo_divergencia}
              onChange={(e) => setDivForm(p => ({ ...p, tipo_divergencia: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel>Prioridade</InputLabel>
              <Select
                label="Prioridade"
                value={divForm.prioridade}
                onChange={(e) => setDivForm(p => ({ ...p, prioridade: e.target.value }))}
              >
                <MenuItem value="critica">Crítica</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="media">Média</MenuItem>
                <MenuItem value="baixa">Baixa</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Valor original (opcional)"
              fullWidth
              value={divForm.valor_original}
              onChange={(e) => setDivForm(p => ({ ...p, valor_original: e.target.value }))}
            />
            <TextField
              label="Descrição *"
              required multiline rows={4} fullWidth
              value={divForm.descricao_divergencia}
              onChange={(e) => setDivForm(p => ({ ...p, descricao_divergencia: e.target.value }))}
              helperText="Mínimo 10 caracteres"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDivModalAberto(false)} disabled={divSalvando}>Cancelar</Button>
          <Button
            variant="contained" color="warning"
            onClick={salvarNovaDivergencia}
            disabled={divSalvando}
            startIcon={<WarningIcon />}
          >
            {divSalvando ? 'Salvando...' : 'Registrar Divergência'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConferenciaFarma;
